import axiosInstance from "./axiosInstance";

export interface ApiModule {
  moduleId: number;
  moduleCode: string;
  moduleName: string;
  description: string;
  isActive: boolean;
  categoryId: number;
}

export interface ApiCategory {
  categoryId: number;
  categoryCode: string;
  categoryName: string;
  displayOrder: number;
  isActive: boolean;
  modules: ApiModule[];
}

export interface ApiRoleModulePermission {
  moduleScreenId: number;
  roleModulePermissionId: number;
  roleId: number;
  moduleId: number;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canLock: boolean;
  canRestore: boolean;
  canManageUsers: boolean;
  isActive: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: number;
  performedBy?: number;
}

/* CREATE / UPDATE types */
export interface SaveRolePermissionPayload {
  roleId: number;
  moduleId: number;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canLock: boolean;
  canRestore: boolean;
  canManageUsers: boolean;
  isActive: boolean;
  updatedBy: number;
  performedBy: number;
}

export interface BulkPermissionItem extends SaveRolePermissionPayload {
  roleModulePermissionId: number;
}

export interface ApiModuleScreen {
  moduleId: number;
  moduleName: string;
  screens: {
    moduleScreenId: number;
    screenName: string;
    shortDescription: string;
    isActive: boolean;
  }[];
}

export interface ApiModule {
  moduleId: number;
  moduleCode: string;
  moduleName: string;
  description: string;
  isActive: boolean;
  categoryId: number ;
  categoryName: string | null;
}

export interface CreateRoleWithPermissionsPayload {
  role: {
    roleId?: number;
    roleCode: string;
    roleName: string;
    description: string;
    isSystemRole: boolean;
    isAvailableForDelegation: boolean;
    defaultDashboardUrl: string;
    isActive?: boolean;     
    createdBy?: number;      
    updatedBy?: number;
  };
  permissions: {
    roleModulePermissionId?: number;
    roleId?: number; 
    moduleId: number;
    moduleScreenId: number;
    canView: boolean;
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canApprove: boolean;
    canLock: boolean;
    canRestore: boolean;
    canManageUsers: boolean;
    isActive: boolean;
    createdBy: number;
  }[];
}



export interface BulkRolePermissionPayload {
  roleId: number;
  items: {
    roleModulePermissionId: number;
    roleId: number;
    moduleId: number;
    moduleScreenId: number;
    canView: boolean;
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canApprove: boolean;
    canLock: boolean;
    canRestore: boolean;
    canManageUsers: boolean;
    isActive: boolean;
    createdBy: number;
  }[];
  performedBy: number;
}

export interface CreateSinglePermissionPayload {
  roleModulePermissionId: number;
  roleId: number;
  moduleId: number;
  moduleScreenId: number;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canLock: boolean;
  canRestore: boolean;
  canManageUsers: boolean;
  isActive: boolean;
  createdBy: number;
}

export const getAuthHeader = () => { 
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// GET categories + modules
export const getCategoriesWithModules = async (
  onlyActive = true
): Promise<ApiCategory[]> => {
  const response = await axiosInstance.get<ApiCategory[]>(
    `/modules/api/admin/categories-modules?onlyActive=${onlyActive}`,{
      headers: getAuthHeader(),
    }
  );
  return response.data;
};

// GET permissions of a role
export const getRolePermissions = async (
  roleId: number
): Promise<ApiRoleModulePermission[]> => {
  const response = await axiosInstance.get<ApiRoleModulePermission[]>(
    `/role-module-permissions/role/${roleId}`, {
      headers: getAuthHeader(),
    }
  );
  return response.data;
};

// CREATE or UPDATE a single permission
export const saveRolePermissions = async (
  payload: SaveRolePermissionPayload
): Promise<ApiRoleModulePermission> => {
  const response = await axiosInstance.post<ApiRoleModulePermission>(
    `/role-module-permissions`,
    payload,{
      headers: getAuthHeader(),
    }
  );
  return response.data;
};

// BULK create permissions
export const saveRolePermissionsBulk = async (
  items: BulkPermissionItem[]
): Promise<{ success: boolean }> => {
  const response = await axiosInstance.post<{ success: boolean }>(
    "/role-module-permissions/bulk",
    { items },{
      headers: getAuthHeader(),
    }
  );
  return response.data;
};

// UPDATE an existing permission
export const updateRoleModulePermission = async (
  roleModulePermissionId: number,
  payload: SaveRolePermissionPayload
): Promise<ApiRoleModulePermission> => {
  const response = await axiosInstance.put<ApiRoleModulePermission>(
    `/role-module-permissions/${roleModulePermissionId}`,
    payload,{
      headers: getAuthHeader(),
    }
  );
  return response.data;
};

export const getModulesWithScreens = async (): Promise<ApiModuleScreen[]> => {
  const res = await axiosInstance.get(
    "/modules/screens?onlyActive=true",{
      headers: getAuthHeader(),
    }
  );
  return res.data;
};

export const getActiveModules = async (): Promise<ApiModule[]> => {
  const res = await axiosInstance.get("/modules", {
    params: { onlyActive: true },
    headers: getAuthHeader(),
  });

  return res.data;
};

export const createRoleWithPermissions = async (
  payload: CreateRoleWithPermissionsPayload
) => {
  const response = await axiosInstance.post(
    "/role-module-permissions/with-permissions",
    payload, {
      headers: getAuthHeader(),
    }
  );

  return response.data;
};

export const saveRolePermissionsBulkV2 = async (
  payload: BulkRolePermissionPayload
) => {
  const response = await axiosInstance.post(
    "/role-module-permissions/bulk",
    payload,{
      headers: getAuthHeader(),
    }
  );

  return response.data;
};

export const createSingleRolePermission = async (
  payload: CreateSinglePermissionPayload
): Promise<ApiRoleModulePermission> => {
  const response = await axiosInstance.post<ApiRoleModulePermission>(
    "/role-module-permissions",
    payload, {
      headers: getAuthHeader(),
    }
  );

  return response.data;
};

export const updateRoleWithPermissions = async (
  payload: CreateRoleWithPermissionsPayload
) => {
  const response = await axiosInstance.put(
    "/role-module-permissions/with-permissions",
    payload,{
      headers: getAuthHeader(),
    }
  );
  return response.data;
};
